import {
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Theme
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { createContext, FC, PropsWithChildren, useContext, useMemo, useState } from "react";
import { Subject } from "rxjs";
import CloseIcon from "@mui/icons-material/Close";
import { OpenGlobalModal } from "./global-modal.types";
import { Heading3 } from "../typography";
import { Button } from "../button";

interface GlobalModalProps {}

const useStylesPaper = makeStyles(({ spacing, shape }: Theme) => ({
  paper: {
    borderRadius: Number(shape.borderRadius) * 2.5,
    minWidth: 300,
    padding: spacing(2)
  }
}));

const useStyles = makeStyles(({ spacing, palette }: Theme) => ({
  actions: {
    "&> *": {
      flexGrow: 1,
      flexBasis: 0
    }
  },
  title: {
    paddingRight: spacing(2)
  },
  primaryBtn: {},
  secondaryBtn: {},
  tertiaryBtn: {},
  content: {
    padding: spacing(2),
    fontSize: 17
  },
  closeButton: {
    color: palette.grey[500]
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }
}));

interface GlobalModalContextInterface extends OpenGlobalModal {
  isOpened: boolean;
  children?: React.ReactNode;
}

const InitialGlobalValue = {
  isOpened: false,
  modalProps: { title: "", content: <></> },
  modalActions: []
};

export const GlobalModalContext = createContext<{
  modalValue: GlobalModalContextInterface;
  setModalValue: (val: GlobalModalContextInterface) => void;
  subject: Subject<any>;
}>({
  modalValue: InitialGlobalValue,
  setModalValue: () => {},
  subject: null as unknown as Subject<any>
});

export const GlobalModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const [modalValue, setModalValue] = useState<GlobalModalContextInterface>(InitialGlobalValue);
  const [subject] = useState(new Subject());

  const value = useMemo(() => {
    return {
      setModalValue,
      modalValue,
      subject
    };
  }, [setModalValue, modalValue, subject]);
  return (
    <GlobalModalContext.Provider value={value}>
      <GlobalModal />
      {children}
    </GlobalModalContext.Provider>
  );
};

export const GlobalModal: React.FC<GlobalModalProps> = () => {
  const { modalValue, setModalValue, subject } = useContext(GlobalModalContext);
  const classes = useStyles();
  const classesPaper = useStylesPaper();

  const onClose = () => {
    setModalValue({ ...modalValue, isOpened: false });
    subject.next(null);
  };

  return (
    <MuiDialog
      onClose={onClose}
      open={modalValue.isOpened}
      classes={classesPaper}
      TransitionProps={{
        onExited: () => setModalValue(InitialGlobalValue)
      }}
    >
      <Grid container justifyContent="space-between" alignItems="center" width="100%">
        {modalValue.modalProps?.title && (
          <Grid item xs={10}>
            <Heading3 m={2} className={classes.title}>
              {modalValue.modalProps?.title}{" "}
            </Heading3>
          </Grid>
        )}
        <Grid item sx={{ ml: "auto" }} xs={2} display="flex" justifyContent="end" alignSelf="start">
          <IconButton onClick={onClose} className={classes.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
      <DialogContent className={classes.content}>{modalValue.modalProps?.content}</DialogContent>
      <DialogActions className={classes.actions}>
        <Grid container spacing={1}>
          {modalValue.modalActions.map((btnConfig) => (
            <Grid item xs={12} key={"" + btnConfig.result}>
              <Button
                variant={btnConfig.variant}
                color={btnConfig.color}
                onClick={() => {
                  setModalValue({ ...modalValue, isOpened: false });
                  subject.next(btnConfig.result);
                }}
                fullWidth
                className={`${
                  classes[
                    (btnConfig.variant + "Btn") as "primaryBtn" | "secondaryBtn" | "tertiaryBtn"
                  ]
                } ${btnConfig.customClass}`}
              >
                {btnConfig.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </DialogActions>
    </MuiDialog>
  );
};
